import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, XCircle, AlertTriangle, Trash2, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'delete';

export interface ToastMessage {
  type: ToastType;
  message: string;
}

interface ToastProps {
  toast: ToastMessage | null;
  onClose: () => void;
  duration?: number;
}

const CONFIG: Record<ToastType, { bg: string; icon: React.ReactNode; label: string }> = {
  success: {
    bg: 'var(--green-500)',
    icon: <CheckCircle size={20} style={{ color: '#ffffff', flexShrink: 0, marginTop: '1px' }} />,
    label: 'Success',
  },
  error: {
    bg: 'var(--red-500)',
    icon: <XCircle size={20} style={{ color: '#ffffff', flexShrink: 0, marginTop: '1px' }} />,
    label: 'Error',
  },
  warning: {
    bg: '#f59e0b',
    icon: <AlertTriangle size={20} style={{ color: '#ffffff', flexShrink: 0, marginTop: '1px' }} />,
    label: 'Warning',
  },
  delete: {
    bg: '#ef4444',
    icon: <Trash2 size={20} style={{ color: '#ffffff', flexShrink: 0, marginTop: '1px' }} />,
    label: 'Deleted',
  },
};

const Toast: React.FC<ToastProps> = ({ toast, onClose, duration = 3000 }) => {
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (!toast || hovered) return;
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [toast, hovered, duration, onClose]);

  useEffect(() => {
    if (!toast) setHovered(false);
  }, [toast]);

  if (!toast) return null;

  const { bg, icon, label } = CONFIG[toast.type];

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: '16px',
        right: '16px',
        zIndex: 10500,
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '14px 16px',
        borderRadius: '8px',
        boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
        border: 'none',
        background: bg,
        minWidth: '280px',
        maxWidth: '380px',
        animation: 'toast-slide-in 0.2s ease',
      }}
      role="alert"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {icon}
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: '13px', color: '#ffffff' }}>
          {label}
        </div>
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.88)', marginTop: '2px' }}>
          {toast.message}
        </div>
      </div>
      <button
        data-testid="BTN_toast_close"
        onClick={onClose}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0',
          color: 'rgba(255,255,255,0.75)',
          flexShrink: 0,
        }}
        aria-label="Close"
      >
        <X size={14} />
      </button>
    </div>,
    document.body
  );
};

export default Toast;
